import {
	bulkCreateCustomDetails,
	getMipCustomDetailsWithoutFormData,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { DataCustomDetailWithoutFormTct } from '@dealstack/shared/lib/generated';
import { saveAs } from 'file-saver';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { CUSTOM_DETAILS_WORKSHEET_NAME } from '@dealstack/shared/lib/common/business/customDetails';
import { useQuery } from '@tanstack/react-query';
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	ColumnDef,
	Table,
} from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import type { RowVirtualizer } from '@dealstack/frontend/shared/utils/reactVirtualTypes';
import { useParams } from 'react-router-dom';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';

enum CustomDetailsColumns {
	INVESTOR_EMAIL = 'INVESTOR_EMAIL',
	NAME = 'NAME',
	VALUE = 'VALUE',
	TYPE = 'TYPE',
	DATA_TYPE = 'DATA_TYPE',
}

export enum CURRENT_VISIBLE_MODAL {
	NONE,
	BULK_CREATE,
}

export const useCustomDetailsController = () => {
	const { mipId } = useParams();
	const toastAPI = useDSToast();
	const [currentVisibleModal, setCurrentVisibleModal] = useState<CURRENT_VISIBLE_MODAL>(
		CURRENT_VISIBLE_MODAL.NONE
	);
	const [sorting, setSorting] = useState<SortingState>([
		{
			desc: false,
			id: CustomDetailsColumns.INVESTOR_EMAIL,
		},
	]);
	const getMipCustomDetailsDataRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.GODMODE_MIP_CUSTOM_DETAILS_WITHOUT_FORM_MIPID_GET, {
				mipId: mipId ?? '',
			}),
		],
		queryFn: ({ signal }) =>
			getMipCustomDetailsWithoutFormData(
				formatUnicorn(API_PATHS.GODMODE_MIP_CUSTOM_DETAILS_WITHOUT_FORM_MIPID_GET, {
					mipId: mipId ?? '',
				}),
				signal
			),
		enabled: !!mipId,
		meta: { suppressErrorToast: true },
	});

	const customDetailsColumns: ColumnDef<DataCustomDetailWithoutFormTct>[] = useMemo(() => {
		return [
			{
				id: CustomDetailsColumns.INVESTOR_EMAIL,
				accessorFn: (row) => row.investorEmail,
				size: 25,
				header: () => <span className="support-app-compact-text">Investor Email</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: CustomDetailsColumns.NAME,
				accessorFn: (row) => row.name,
				size: 25,
				header: () => <span className="support-app-compact-text">Custom details name</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: CustomDetailsColumns.VALUE,
				accessorFn: (row) => row.value,
				size: 25,
				header: () => (
					<span className="support-app-compact-text">Custom details value</span>
				),
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: CustomDetailsColumns.TYPE,
				accessorFn: (row) => row.type,
				size: 25,
				header: () => <span className="support-app-compact-text">Custom details type</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: CustomDetailsColumns.DATA_TYPE,
				accessorFn: (row) => row.dataType,
				size: 25,
				header: () => (
					<span className="support-app-compact-text">Custom details data type</span>
				),
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
		];
	}, [getMipCustomDetailsDataRequest?.isLoading]);

	const customDetailsData = getMipCustomDetailsDataRequest?.data?.payload ?? [];

	const tanstackTable: Table<DataCustomDetailWithoutFormTct> = useReactTable({
		data: customDetailsData,
		columns: customDetailsColumns,
		enableSorting: true,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLTableElement>(null);

	const rowVirtualizer: RowVirtualizer = useVirtual({
		parentRef: tableContainerRef,
		size: getMipCustomDetailsDataRequest?.data?.payload?.length ?? 0,
		overscan: 10,
	});

	const bulkCreateUpload = useWrappedRequest(
		bulkCreateCustomDetails(mipId ?? ''),
		{},
		{
			onSuccessDefault: () => {
				// oxlint-disable-next-line typescript/no-floating-promises
				getMipCustomDetailsDataRequest.refetch();
				setCurrentVisibleModal(CURRENT_VISIBLE_MODAL.NONE);
				toastAPI.success({});
			},
			onLocalErrorDefault: ({ error }) => {
				const message = error?.code ? error.message : 'Error during uploading file';
				toastAPI.error({ message });
				logger.error(`Bulk create custom details:  ${error}`);
			},
		}
	);

	const onDownloadXLSXTemplate = async (): Promise<void> => {
		const ExcelJS = await asyncLoadExceljs();
		const workbook = new ExcelJS.Workbook();
		workbook.created = new Date();

		const dataWorksheet = workbook.addWorksheet(CUSTOM_DETAILS_WORKSHEET_NAME);

		const columnsName = [
			'email',
			'<custom detail name>',
			'custom detail type (INDIVIDUAL or VEHICLE)',
		];

		dataWorksheet.columns = columnsName.map((name) => ({
			header: name,
			key: name,
			width: 40,
		}));

		dataWorksheet.addRows([
			{
				[columnsName[0]]: '<email>',
				[columnsName[1]]: '<custom detail value>',
				[columnsName[2]]: '<custom detail type>',
			},
		]);

		const xls64 = await workbook.xlsx.writeBuffer();

		saveAs(
			new Blob([xls64], {
				type: EXCEL_SPREADSHEET_TYPE,
			}),
			`create-custom-details-mip-${mipId}.xlsx`
		);
	};

	return {
		currentVisibleModal,
		setCurrentVisibleModal,
		isDataLoading: getMipCustomDetailsDataRequest?.isLoading,
		refreshData: getMipCustomDetailsDataRequest.refetch,
		tableContainerRef,
		tanstackTable,
		rowVirtualizer,
		bulkCreateUpload,
		onDownloadXLSXTemplate,
		customDetailsData,
	};
};
